/**
 * StockLevelBar Component
 *
 * Progress bar component showing current stock level relative to reorder point.
 * Uses color coding to indicate severity:
 * - Green: >50% of reorder point (healthy stock)
 * - Amber: 25-50% of reorder point (getting low)
 * - Red: <25% of reorder point (critical)
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 4: Stock Overview Dashboard
 */

import { cn } from '@/lib/utils'

/**
 * Get the color class based on stock level percentage
 *
 * @param {number} percentage - Stock percentage relative to reorder point
 * @returns {string} - Tailwind CSS color class for the progress bar
 */
function getStockLevelColor(percentage) {
  if (percentage >= 50) {
    return 'bg-emerald-500'
  }
  if (percentage >= 25) {
    return 'bg-amber-500'
  }
  return 'bg-red-500'
}

/**
 * Get the background color class based on stock level percentage
 *
 * @param {number} percentage - Stock percentage relative to reorder point
 * @returns {string} - Tailwind CSS background color class
 */
function getBackgroundColor(percentage) {
  if (percentage >= 50) {
    return 'bg-emerald-100'
  }
  if (percentage >= 25) {
    return 'bg-amber-100'
  }
  return 'bg-red-100'
}

/**
 * StockLevelBar Component
 *
 * @param {Object} props
 * @param {number} props.currentStock - Current stock quantity
 * @param {number} props.reorderPoint - Reorder point threshold
 * @param {string} props.unit - Unit of measure (e.g., 'kg', 'units')
 * @param {boolean} props.showLabel - Whether to show the stock/reorder label
 * @param {string} props.className - Additional CSS classes
 */
export default function StockLevelBar({
  currentStock = 0,
  reorderPoint = 0,
  unit = '',
  showLabel = true,
  className = ''
}) {
  // Calculate percentage (use reorder point as 100% reference)
  // Stock above reorder point is >100%, cap display at 100%
  // Clamp between 0 and 100
  const rawPercentage = reorderPoint > 0
    ? (currentStock / reorderPoint) * 100
    : currentStock > 0 ? 100 : 0
  const percentage = Math.max(0, Math.min(100, rawPercentage))

  const displayPercentage = Math.round(percentage)
  const colorClass = getStockLevelColor(percentage)
  const bgClass = getBackgroundColor(percentage)

  // Format numbers for display
  const formatNumber = (num) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`
    }
    return Number.isInteger(num) ? num.toString() : num.toFixed(1)
  }

  return (
    <div className={cn('space-y-1', className)}>
      {showLabel && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-foreground font-medium">
            {formatNumber(currentStock)}{unit && ` ${unit}`}
          </span>
          <span className="text-muted-foreground">
            / {formatNumber(reorderPoint)}{unit && ` ${unit}`}
          </span>
        </div>
      )}

      {/* Progress bar container */}
      <div
        className={cn('relative h-2 w-full overflow-hidden rounded-full', bgClass)}
        role="progressbar"
        aria-valuenow={displayPercentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Stock level: ${formatNumber(currentStock)} of ${formatNumber(reorderPoint)} ${unit}`}
      >
        {/* Progress bar fill */}
        <div
          className={cn(
            'h-full transition-all duration-300 ease-in-out rounded-full',
            colorClass
          )}
          style={{ width: `${displayPercentage}%` }}
        />
      </div>
    </div>
  )
}
